import type { WorkoutSegment, SportType } from '@/types';

// Garmin FIT Protocol Constants
const CRC_TABLE = [
  0x0000, 0xCC01, 0xD801, 0x1400, 0xF001, 0x3C00, 0x2800, 0xE401,
  0xA001, 0x6C00, 0x7800, 0xB401, 0x5000, 0x9C01, 0x8801, 0x4400
];

function updateCRC(crc: number, byte: number): number {
  let tmp = CRC_TABLE[crc & 0xF];
  crc = (crc >> 4) & 0x0FFF;
  crc = crc ^ tmp ^ CRC_TABLE[byte & 0xF];

  tmp = CRC_TABLE[crc & 0xF];
  crc = (crc >> 4) & 0x0FFF;
  crc = crc ^ tmp ^ CRC_TABLE[(byte >> 4) & 0xF];
  
  return crc;
}

function calculateCRC(data: Uint8Array, start: number, end: number): number {
  let crc = 0;
  for (let i = start; i < end; i++) {
    crc = updateCRC(crc, data[i]);
  }
  return crc;
}

// Normalize strings to ASCII - STRICT (A-Z, 0-9, space, dash only)
function normalizeString(str: string): string {
  const map: {[key: string]: string} = {
    'ą': 'a', 'ć': 'c', 'ę': 'e', 'ł': 'l', 'ń': 'n', 'ó': 'o', 'ś': 's', 'ź': 'z', 'ż': 'z',
    'Ą': 'A', 'Ć': 'C', 'Ę': 'E', 'Ł': 'L', 'Ń': 'N', 'Ó': 'O', 'Ś': 'S', 'Ź': 'Z', 'Ż': 'Z'
  };
  // Replace Polish chars, then remove anything that isn't alphanumeric, space, or dash.
  // Removing parens () and other punctuation to be super safe.
  return str.replace(/[ąćęłńóśźżĄĆĘŁŃÓŚŹŻ]/g, match => map[match] || match)
            .replace(/[^a-zA-Z0-9 \-]/g, ''); 
}

// Helper: Clean leading dashes/arrows from text
function cleanLine(str: string): string {
  if (!str) return "";
  return str.replace(/^[->\s]+/, '').trim();
}

// Base Types
const BT_ENUM = 0x00;
const BT_UINT8 = 0x02;
const BT_UINT16 = 0x04;
const BT_UINT32 = 0x06;
const BT_STRING = 0x07;

class FitWriter {
  private buffer: Uint8Array;
  private view: DataView;
  private offset: number;

  constructor(size: number = 1024 * 10) {
    this.buffer = new Uint8Array(size);
    this.view = new DataView(this.buffer.buffer);
    this.offset = 0;
  }

  ensureCapacity(bytes: number) {
    if (this.offset + bytes > this.buffer.length) {
      const newBuffer = new Uint8Array(this.buffer.length * 2);
      newBuffer.set(this.buffer);
      this.buffer = newBuffer;
      this.view = new DataView(this.buffer.buffer);
    }
  }

  writeByte(val: number) {
    this.ensureCapacity(1);
    this.view.setUint8(this.offset, val);
    this.offset += 1;
  }

  writeUInt16(val: number) {
    this.ensureCapacity(2);
    this.view.setUint16(this.offset, val, true); // Little Endian
    this.offset += 2;
  }

  writeUInt32(val: number) {
    this.ensureCapacity(4);
    this.view.setUint32(this.offset, val, true); // Little Endian
    this.offset += 4;
  }

  writeString(str: string, length: number) {
    this.ensureCapacity(length);
    // CRITICAL: Always reserve at least 1 byte for null terminator
    const asciiStr = normalizeString(str).substring(0, length - 1); 
    
    for (let i = 0; i < length; i++) {
      if (i < asciiStr.length) {
        this.view.setUint8(this.offset + i, asciiStr.charCodeAt(i));
      } else {
        this.view.setUint8(this.offset + i, 0); // Zero padding / Null terminator
      }
    }
    this.offset += length;
  }

  writeBytes(bytes: Uint8Array) {
    this.ensureCapacity(bytes.length);
    this.buffer.set(bytes, this.offset);
    this.offset += bytes.length;
  }

  getData(): Uint8Array {
    return this.buffer.slice(0, this.offset);
  }
}

// FIT Message Definitions
const MSG_FILE_ID = 0;
/** Declares authoring software — improves compatibility with Garmin Connect mobile imports. */
const MSG_FILE_CREATOR = 49;
const MSG_WORKOUT = 26;
const MSG_WORKOUT_STEP = 27;

// Types
const TYPE_WORKOUT = 5;
const MFR_GARMIN = 1;
const MFR_DEVELOPMENT = 255; 

// Sport
const SPORT_RUNNING = 1;
const SPORT_CYCLING = 2;

// Step Intensity
const INTENSITY_ACTIVE = 0;
const INTENSITY_REST = 1;
const INTENSITY_WARMUP = 2;
const INTENSITY_COOLDOWN = 3;

// Duration Type
const DURATION_TIME = 0;

// Target Type (Wkt Step Target)
const TARGET_SPEED = 0;
const TARGET_OPEN = 2; 
const TARGET_POWER = 4;

// Default FTP when none supplied (watts)
const DEFAULT_FTP_WATTS = 200;

export interface GenerateFitFileOptions {
  /** Cycling power targets: segment powerLow/powerHigh are interpreted as %FTP × this value. */
  ftpWatts?: number;
}

/**
 * Target fields for a single `workout_step` data record (power/speed/open).
 * Exported for unit tests — must stay aligned with the writer loop in `generateFitFile`.
 */
export function workoutStepTargetFieldsForSegment(
  seg: WorkoutSegment,
  sport: SportType,
  ftpWatts: number,
): { targetType: number; valLow: number; valHigh: number } {
  let targetType = TARGET_OPEN;
  let valLow = 0;
  let valHigh = 0;

  if (sport === 'run' && seg.powerHigh < 8 && seg.powerHigh > 0) {
    targetType = TARGET_SPEED;
    valLow = Math.round(seg.powerLow * 1000);
    valHigh = Math.round(seg.powerHigh * 1000);
  } else if (seg.powerHigh > 0) {
    targetType = TARGET_POWER;
    valLow = Math.round(seg.powerLow * ftpWatts);
    valHigh = Math.round(seg.powerHigh * ftpWatts);
  }

  const MAX_TARGET_VAL = 1000000;

  if (!isFinite(valLow) || valLow < 0) valLow = 0;
  if (valLow > MAX_TARGET_VAL) valLow = 0;

  if (!isFinite(valHigh) || valHigh < 0) valHigh = 0;
  if (valHigh > MAX_TARGET_VAL) valHigh = 0;

  if (valHigh < valLow) valHigh = valLow;

  if (valHigh <= 0) {
    targetType = TARGET_OPEN;
    valLow = 0;
    valHigh = 0;
  }

  return { targetType, valLow, valHigh };
}

/** ASCII-safe basename for `.fit` downloads (no Polish diacritics, spaces → underscores). */
export function normalizeFitWorkoutFileBasename(name: string): string {
  const clean = cleanLine(name);
  return normalizeString(clean).replace(/ /g, '_').toLowerCase();
}

export const generateFitFile = (
  segments: WorkoutSegment[],
  name: string,
  sport: SportType,
  options?: GenerateFitFileOptions,
): Uint8Array => {
  const ftpWatts =
    typeof options?.ftpWatts === 'number' && Number.isFinite(options.ftpWatts) && options.ftpWatts > 0
      ? options.ftpWatts
      : DEFAULT_FTP_WATTS;
  const validSegments = segments.filter(s => s.duration > 0);
  if (validSegments.length === 0) {
    throw new Error('FIT_GENERATION_EMPTY_STEPS');
  }
  const writer = new FitWriter();

  // -------------------------------------------------------------------
  // 1. File Header (14 bytes)
  // -------------------------------------------------------------------
  const headerSize = 14;
  const dataSizeOffset = 4;
  
  writer.writeByte(headerSize);
  writer.writeByte(0x10); // Protocol Version 1.0 (Lower version for compatibility)
  writer.writeUInt16(2033); // Profile Ver 20.33
  writer.writeUInt32(0); // Data Size (Placeholder)
  
  // ".FIT" signature
  writer.writeByte(46); writer.writeByte(70); writer.writeByte(73); writer.writeByte(84);
  
  writer.writeUInt16(0); // Header CRC (Placeholder)

  const dataStart = writer.getData().length;

  // -------------------------------------------------------------------
  // 2. Data Records
  // -------------------------------------------------------------------

  // --- Definition Message: FILE ID (Local ID 0) ---
  writer.writeByte(0x40 | 0);
  writer.writeByte(0);
  writer.writeByte(0); 
  writer.writeUInt16(MSG_FILE_ID);
  writer.writeByte(5); 
  
  // Fields: [Field Num, Size, Base Type]
  writer.writeByte(0); writer.writeByte(1); writer.writeByte(BT_ENUM); // Type
  writer.writeByte(1); writer.writeByte(2); writer.writeByte(BT_UINT16); // Manufacturer
  writer.writeByte(2); writer.writeByte(2); writer.writeByte(BT_UINT16); // Product
  writer.writeByte(3); writer.writeByte(4); writer.writeByte(BT_UINT32); // Serial Number
  writer.writeByte(4); writer.writeByte(4); writer.writeByte(BT_UINT32); // Time Created

  // --- Data Message: FILE ID ---
  const timeCreated = Math.max(1, Math.floor(Date.now() / 1000) - 631065600);
  const serialNumber = Math.floor(Math.random() * 0xFFFFFF) + 1; 

  writer.writeByte(0); 
  writer.writeByte(TYPE_WORKOUT); 
  writer.writeUInt16(MFR_GARMIN); // Use Garmin Mfr ID (1) for compatibility
  writer.writeUInt16(1); // Product ID 1
  writer.writeUInt32(serialNumber);
  writer.writeUInt32(timeCreated);

  // --- Definition Message: FILE CREATOR (Local ID 3) ---
  writer.writeByte(0x40 | 3);
  writer.writeByte(0);
  writer.writeByte(0);
  writer.writeUInt16(MSG_FILE_CREATOR);
  writer.writeByte(2);
  writer.writeByte(0);
  writer.writeByte(2);
  writer.writeByte(BT_UINT16);
  writer.writeByte(1);
  writer.writeByte(1);
  writer.writeByte(BT_UINT8);

  // --- Data Message: FILE CREATOR ---
  writer.writeByte(3);
  writer.writeUInt16(100);
  writer.writeByte(0);

  // --- Definition Message: WORKOUT (Local ID 1) ---
  writer.writeByte(0x40 | 1); 
  writer.writeByte(0);
  writer.writeByte(0);
  writer.writeUInt16(MSG_WORKOUT);
  writer.writeByte(4); 
  
  writer.writeByte(4); writer.writeByte(1); writer.writeByte(BT_ENUM); // Sport
  writer.writeByte(5); writer.writeByte(1); writer.writeByte(BT_ENUM); // Sub Sport
  writer.writeByte(6); writer.writeByte(2); writer.writeByte(BT_UINT16); // Num Steps
  writer.writeByte(8); writer.writeByte(16); writer.writeByte(BT_STRING); // Name

  // --- Data Message: WORKOUT ---
  const sportId = sport === 'run' ? SPORT_RUNNING : SPORT_CYCLING;
  
  writer.writeByte(1); 
  writer.writeByte(sportId);
  writer.writeByte(0); 
  writer.writeUInt16(validSegments.length);
  
  const cleanName = cleanLine(name);
  writer.writeString(cleanName, 16); 

  // --- Definition Message: WORKOUT STEP (Local ID 2) ---
  writer.writeByte(0x40 | 2);
  writer.writeByte(0);
  writer.writeByte(0);
  writer.writeUInt16(MSG_WORKOUT_STEP);
  writer.writeByte(9); 
  
  // Fields definitions
  writer.writeByte(0);   writer.writeByte(16); writer.writeByte(BT_STRING); // Name
  writer.writeByte(1);   writer.writeByte(1); writer.writeByte(BT_ENUM);   // Duration Type
  writer.writeByte(2);   writer.writeByte(4); writer.writeByte(BT_UINT32); // Duration Value
  writer.writeByte(3);   writer.writeByte(1); writer.writeByte(BT_ENUM);   // Target Type
  writer.writeByte(4);   writer.writeByte(4); writer.writeByte(BT_UINT32); // Target Value
  writer.writeByte(5);   writer.writeByte(4); writer.writeByte(BT_UINT32); // Custom Target Low
  writer.writeByte(6);   writer.writeByte(4); writer.writeByte(BT_UINT32); // Custom Target High
  writer.writeByte(7);   writer.writeByte(1); writer.writeByte(BT_ENUM);   // Intensity
  writer.writeByte(254); writer.writeByte(2); writer.writeByte(BT_UINT16); // Message Index

  // --- Data Messages: WORKOUT STEPS ---
  validSegments.forEach((seg, index) => {
    writer.writeByte(2); 
    
    // 1. Name
    let stepName = seg.text || seg.type;
    stepName = cleanLine(stepName);
    stepName = stepName.replace(/IntervalsT/g, "Run").replace(/SteadyState/g, "Run");
    // Ensure strict length
    writer.writeString(stepName, 16);
    
    // 2. Duration Type (Always Time=0)
    writer.writeByte(DURATION_TIME);

    // 3. Duration Value (ms)
    let durationSeconds = seg.duration;
    if (!isFinite(durationSeconds) || durationSeconds < 1) durationSeconds = 300;
    if (durationSeconds > 86400) durationSeconds = 86400; 
    
    const durationMs = Math.round(durationSeconds * 1000);
    writer.writeUInt32(durationMs);

    const { targetType, valLow, valHigh } = workoutStepTargetFieldsForSegment(seg, sport, ftpWatts);

    writer.writeByte(targetType);      // 4. Target Type
    
    // 5. Target Value
    // If we have a range (Custom Low/High), it's safer to set Target Value to 0 
    // to avoid confusing parsers that might apply offset/scale to this field.
    writer.writeUInt32(0);     
    
    writer.writeUInt32(valLow);        // 6. Custom Low
    writer.writeUInt32(valHigh);       // 7. Custom High

    // 8. Intensity
    let intensity = INTENSITY_ACTIVE;
    const lowerText = (seg.text || "").toLowerCase();
    
    if (seg.type === 'Warmup') intensity = INTENSITY_WARMUP;
    else if (seg.type === 'Cooldown') intensity = INTENSITY_COOLDOWN;
    else if (lowerText.includes('recovery') || lowerText.includes('rest') || lowerText.includes('przerwa') || lowerText.includes('odpoczynek') || lowerText.includes('regeneracja')) {
        intensity = INTENSITY_REST;
    }
    
    writer.writeByte(intensity);
    
    // 9. Message Index
    writer.writeUInt16(index);
  });

  // -------------------------------------------------------------------
  // End & CRC
  // -------------------------------------------------------------------

  const dataEnd = writer.getData().length;
  const dataSize = dataEnd - dataStart;

  // Update Data Size in Header
  const fullBuffer = writer.getData();
  const view = new DataView(fullBuffer.buffer);
  view.setUint32(dataSizeOffset, dataSize, true);

  // Calculate Header CRC
  const headerCrc = calculateCRC(fullBuffer, 0, 12);
  view.setUint16(12, headerCrc, true);

  // Calculate File CRC
  const fileCrc = calculateCRC(fullBuffer, 0, fullBuffer.length);
  
  const finalWriter = new FitWriter(fullBuffer.length + 2);
  finalWriter.writeBytes(fullBuffer);
  finalWriter.writeUInt16(fileCrc);

  return finalWriter.getData();
};

export const downloadFit = (segments: WorkoutSegment[], name: string, sport: SportType, options?: GenerateFitFileOptions) => {
  const bytes = generateFitFile(segments, name, sport, options);
  const blob = new Blob([bytes], { type: 'application/octet-stream' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${normalizeFitWorkoutFileBasename(name)}.fit`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};